import {
  resolveGuestDeepLinkTarget,
  toGuestJourneyPath,
  type GuestJourneySummaryRecord,
} from '@tov-plus/domain';

import {
  BrowserHttpError,
  NetworkUnavailableError,
} from '../lib/http/createBrowserHttpClient';
import { msg } from '@tov-plus/ui';

export interface OccasionSlugResolutionInput {
  hostname: string;
  pathname: string;
  search: string;
  envOccasionSlug: string | null;
}

export interface GuestTokenResolutionInput {
  search: string;
  storedToken: string | null;
  envToken: string | null;
}

export type GuestAccessFailureKind =
  'invalid_token' | 'occasion_unavailable' | 'access_unavailable';

export interface GuestEntryIntent {
  source:
    | 'invite'
    | 'reminder'
    | 'schedule_update'
    | 'seating_update'
    | 'gallery_update'
    | 'returning_session'
    | 'direct';
  requestedTarget: string | null;
  requestedLocaleCode: string | null;
}

export interface ReviewPassResolution {
  token: string;
  previewMode:
    | 'public_landing'
    | 'invited_guest'
    | 'rsvp_pending'
    | 'rsvp_complete'
    | 'event_day';
  expiresAt: string;
  isValid: boolean;
}

export const PUBLIC_GUEST_ACCESS_SENTINEL = '__public__';
export const PLANNER_GUEST_VIEW_AS_PREFIX = '__planner_actor__:';

export function resolvePlannerGuestViewAs(search: string): {
  mode: 'public' | 'guest';
  actorId: string | null;
} | null {
  const url = new URL(`https://tov.plus${search}`);
  const mode = url.searchParams.get('view_as')?.trim() ?? null;
  const actorId = url.searchParams.get('actor_id')?.trim() ?? null;

  if (mode === 'public') {
    return {
      mode: 'public',
      actorId: null,
    };
  }

  if (mode === 'guest' && actorId) {
    return {
      mode: 'guest',
      actorId,
    };
  }

  return null;
}

export function resolveResumeCode(search: string): string | null {
  const url = new URL(`https://tov.plus${search}`);
  const code = url.searchParams.get('resume_code')?.trim() ?? null;
  return code && code.length > 0 ? code : null;
}

const guestRouteSegments = new Set([
  'events',
  'today',
  'messages',
  'my-invite',
  'recent-invites',
  'review',
  'locations',
  'travel-packet',
  'rsvp',
  'forms',
  'registry',
  'gallery',
  'follow-up',
  'schedule',
  'shared-planning',
  'seating',
  'help',
  'access',
  '__open-invitation-playground',
]);

function resolveOccasionSlugFromPathname(pathname: string): string | null {
  const firstSegment = pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)[0];

  if (!firstSegment || guestRouteSegments.has(firstSegment)) {
    return null;
  }

  try {
    return decodeURIComponent(firstSegment);
  } catch {
    return null;
  }
}

export function resolveGuestRouterBasename(pathname: string): string {
  const occasionSlug = resolveOccasionSlugFromPathname(pathname);

  return occasionSlug ? `/${encodeURIComponent(occasionSlug)}` : '';
}

export function resolveOccasionSlug(
  input: OccasionSlugResolutionInput,
): string | null {
  const pathnameValue = resolveOccasionSlugFromPathname(input.pathname);

  if (pathnameValue && pathnameValue.trim().length > 0) {
    return pathnameValue.trim();
  }

  const url = new URL(`https://tov.plus${input.search}`);
  const searchValue = url.searchParams.get('occasion');

  if (searchValue && searchValue.trim().length > 0) {
    return searchValue.trim();
  }

  return input.envOccasionSlug;
}

export function resolveGuestToken(
  input: GuestTokenResolutionInput,
): string | null {
  const url = new URL(`https://tov.plus${input.search}`);
  const queryToken =
    url.searchParams.get('guest_token') ?? url.searchParams.get('token');

  if (queryToken && queryToken.trim().length > 0) {
    return queryToken.trim();
  }

  if (input.storedToken && input.storedToken.trim().length > 0) {
    return input.storedToken.trim();
  }

  return input.envToken;
}

export function resolveGuestAccessToken(input: GuestTokenResolutionInput) {
  const plannerViewAs = resolvePlannerGuestViewAs(input.search);

  if (plannerViewAs) {
    return plannerViewAs.mode === 'guest' && plannerViewAs.actorId
      ? `${PLANNER_GUEST_VIEW_AS_PREFIX}${plannerViewAs.actorId}`
      : PUBLIC_GUEST_ACCESS_SENTINEL;
  }

  return resolveGuestToken(input);
}

export function resolveReviewPass(search: string): ReviewPassResolution | null {
  const url = new URL(`https://tov.plus${search}`);
  const token = url.searchParams.get('review_token')?.trim() ?? null;
  const previewMode = url.searchParams.get('preview_mode')?.trim() ?? null;

  if (!token || !previewMode) {
    return null;
  }

  const tokenMatch = token.match(/^review_(\d{10,})_([a-z0-9]+)$/i);

  if (!tokenMatch) {
    return {
      token,
      previewMode: 'public_landing',
      expiresAt: '',
      isValid: false,
    };
  }

  const expiresAt = new Date(Number(tokenMatch[1])).toISOString();
  const allowedPreviewMode =
    previewMode === 'public_landing' ||
    previewMode === 'invited_guest' ||
    previewMode === 'rsvp_pending' ||
    previewMode === 'rsvp_complete' ||
    previewMode === 'event_day'
      ? previewMode
      : null;

  return {
    token,
    previewMode: allowedPreviewMode ?? 'public_landing',
    expiresAt,
    isValid: allowedPreviewMode !== null && Number(tokenMatch[1]) > Date.now(),
  };
}

export function resolveGuestEntryIntent(search: string): GuestEntryIntent {
  const url = new URL(`https://tov.plus${search}`);
  const entrySource = url.searchParams.get('entry')?.trim().toLowerCase();

  return {
    source:
      entrySource === 'invite' ||
      entrySource === 'reminder' ||
      entrySource === 'schedule_update' ||
      entrySource === 'seating_update' ||
      entrySource === 'gallery_update' ||
      entrySource === 'returning_session'
        ? entrySource
        : 'direct',
    requestedTarget: url.searchParams.get('target')?.trim() ?? null,
    requestedLocaleCode: url.searchParams.get('locale')?.trim() ?? null,
  };
}

export function resolveGuestEntryRedirect(input: {
  search: string;
  summary: GuestJourneySummaryRecord;
}) {
  const intent = resolveGuestEntryIntent(input.search);
  const resolution = resolveGuestDeepLinkTarget({
    requestedTarget: intent.requestedTarget,
    summary: input.summary,
  });

  return {
    intent,
    target: resolution.target,
    path: toGuestJourneyPath(resolution.target),
    didFallback: resolution.didFallback,
    reason: resolution.reason,
  };
}

export function getGuestResumeStorageKey(
  occasionSlug: string,
  guestToken: string,
) {
  return `tov-plus.guests.resume:${occasionSlug}:${guestToken}`;
}

export function shouldPersistGuestPath(pathname: string) {
  return pathname !== '/access' && pathname !== '/';
}

export function shouldRedirectMissingSlugToMarketing(pathname: string) {
  const normalizedPathname = pathname.trim();

  return normalizedPathname === '' || normalizedPathname === '/';
}

export function resolveGuestResumePath(input: {
  storedPath: string | null;
  summary: GuestJourneySummaryRecord;
}) {
  if (!input.storedPath || input.storedPath === '/') {
    return null;
  }

  const candidateTarget = input.storedPath.replace(/^\//, '').replace('-', '_');
  const resolution = resolveGuestDeepLinkTarget({
    requestedTarget:
      candidateTarget === '' ? msg('app.access.home') : candidateTarget,
    summary: input.summary,
  });

  return resolution.didFallback ? null : toGuestJourneyPath(resolution.target);
}

export function classifyGuestAccessFailure(error: unknown): {
  kind: GuestAccessFailureKind;
  message: string;
} {
  if (error instanceof BrowserHttpError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return {
        kind: 'invalid_token',
        message: msg(
          'app.access.this_invite_link_is_invalid_expired_or_no_longer_allowed_for_this',
        ),
      };
    }

    if (error.statusCode === 404) {
      return {
        kind: 'occasion_unavailable',
        message: msg(
          'app.access.this_occasion_link_is_no_longer_available_or_the_host_has_changed',
        ),
      };
    }

    return {
      kind: 'access_unavailable',
      message: error.message,
    };
  }

  if (error instanceof Error) {
    return {
      kind: 'access_unavailable',
      message: error.message,
    };
  }

  return {
    kind: 'access_unavailable',
    message: msg(
      'app.access.the_guest_shell_could_not_resolve_the_occasion_experience',
    ),
  };
}

export function shouldRenderGuestAccessOffline(error: unknown): boolean {
  return (
    error instanceof NetworkUnavailableError &&
    error.reason === 'browser_offline'
  );
}

const DISTRIBUTION_TOKEN_PREFIX = 'dst_';
const GUEST_SESSION_TOKEN_PREFIX = 'gss_';

export function isDistributionToken(token: string, urlSearch: string): boolean {
  if (token.startsWith(GUEST_SESSION_TOKEN_PREFIX)) {
    return false;
  }
  const params = new URLSearchParams(urlSearch);
  const urlToken = params.get('guest_token') ?? params.get('token');
  if (urlToken && urlToken === token) {
    return true;
  }
  if (token.startsWith(DISTRIBUTION_TOKEN_PREFIX)) {
    return true;
  }
  return false;
}

export function removeTokenFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('guest_token');
  url.searchParams.delete('token');
  url.searchParams.delete('resume_code');
  window.history.replaceState(null, '', url.toString());
  window.dispatchEvent(new CustomEvent('tov:guest-access-cleared'));
}
